/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle } from 'lucide-react';
import { useHelpStore } from '@/store/helpStore';

/**
 * Opens the help drawer. Rendered in more than one place because the desktop header is hidden below
 * 768px and the mobile header is a separate subtree — each variant styles itself for where it sits.
 *
 * Renders nothing until the drawer reports that the current page actually has a help card.
 */
export const HelpButton: React.FC<{
  variant?: 'header' | 'mobile' | 'floating' | 'menu' | 'pin';
  className?: string;
}> = ({ variant = 'header', className }) => {
  const { t } = useTranslation();
  const { open, hasCard } = useHelpStore();

  if (!hasCard) return null;

  const label = t('help.button', 'Help');
  const title = t('help.buttonTitle', 'What to do on this page');

  const classes: Record<string, string> = {
    header: 'header-nav-link mr-4 inline-flex items-center gap-1',
    // Sits next to the burger in the dark mobile bar, so it inherits white-on-dark.
    mobile: 'inline-flex items-center justify-center p-2 text-white/90 hover:text-white',
    menu: 'flex items-center gap-2 py-2 text-sm text-slate-200 hover:text-white w-full',
    // Tiny icon next to the floating header pin, visible only while the header is collapsed.
    pin: 'pin-floating pin-action pin-action-help',
    floating:
      'fixed bottom-6 right-6 z-40 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700',
  };

  return (
    <button
      type="button"
      onClick={open}
      title={title}
      aria-label={title}
      className={`${classes[variant]} ${className ?? ''}`}
    >
      <HelpCircle className={variant === 'floating' ? 'h-6 w-6' : 'h-5 w-5'} />
      {variant === 'header' && <span className="hidden md:inline">{label}</span>}
      {variant === 'menu' && <span>{label}</span>}
    </button>
  );
};

export default HelpButton;
